/**
 * The geometry and timing behind going through a Portal.
 * Handoff: docs/plans/2026-09-portals-stories/handoff-transitions/README.md.
 *
 * Every route change in the app goes through a ring: it opens where you tapped and the next
 * page is revealed through its hole. Opening a Portal is the same motion with the tapped ring
 * itself as the doorway.
 *
 * The numbers live here, apart from the DOM, because they are the part that has to be exactly
 * right — the clip has to stay under the ring's band at every frame, or you see a hard edge.
 */

/** The band runs 0.875r → 1.00r of the asset, so the hole is 0.875 of the ring's radius. */
export const HOLE_RATIO = 0.875;

/** Pulled in: slow, then accelerating. */
export const EASE_FORWARD = 'cubic-bezier(0.55, 0, 0.7, 0.2)';
/** Thrown back out: fast, then settling. */
export const EASE_BACK = 'cubic-bezier(0.3, 0.8, 0.45, 1)';

export const PAGE_MS = 640;
/** The viewer always takes 1.2× the page duration. */
export const VIEWER_MS = 770;
export const REDUCED_MS = 160;

export const PAGE_RING_PX = 56;
export const RAIL_RING_PX = 64;

export const START_SCALE = 0.2;        // the ring's scale before it opens
export const OLD_PAGE_SCALE = 0.92;    // what the page you're leaving shrinks to
export const NEW_PAGE_SCALE = 1.14;    // what the page you're arriving at comes in from
export const DIM_OPACITY = 0.55;

export const APP_SCALE_VIEWER = 0.9;   // the whole app, behind an opening Portal
export const VIEWER_SCALE = 1.3;
export const VIEWER_DIM = 0.6;

/** The ring fades over the last 30% of a forward move, and in over the first 30% coming back. */
export const FADE_FRACTION = 0.3;

/**
 * How far the hole has to travel to clear the farthest corner of the box.
 *
 * 1.04 is the handoff's margin: without it the clip edge lands exactly on the corner and a
 * sliver of the old page survives for a frame.
 */
export function coverRadius(x, y, width, height) {
  const dx = Math.max(x, width - x);
  const dy = Math.max(y, height - y);
  return Math.hypot(dx, dy) * 1.04;
}

/**
 * Everything the layers need for one move.
 *
 * @param origin  {x, y} inside the box — where the finger went down
 * @param box     {width, height} of the area that transitions
 * @param ringPx  the ring's box at rest (56 for a page, 64 for a rail ring)
 */
export function portalGeometry(origin, box, ringPx = PAGE_RING_PX) {
  const hole = (ringPx / 2) * HOLE_RATIO;
  const R = coverRadius(origin.x, origin.y, box.width, box.height);
  return { hole, R, endScale: R / hole, startRadius: hole * START_SCALE };
}

/** `circle(12.5px at 40px 90px)` — what the incoming surface is revealed through. */
export function clipCircle(radius, origin) {
  return `circle(${radius}px at ${origin.x}px ${origin.y}px)`;
}

/** The ring's own box, centred on the origin. */
export function ringBox(origin, ringPx = PAGE_RING_PX) {
  return { left: origin.x - ringPx / 2, top: origin.y - ringPx / 2, width: ringPx, height: ringPx };
}

/**
 * When the ring's fade runs, as a CSS animation delay and duration.
 * Forward it leaves over the last 30%; coming back it arrives over the first 30%.
 */
export function fadeTiming(durationMs, back = false) {
  const span = Math.round(durationMs * FADE_FRACTION);
  return { delay: back ? 0 : durationMs - span, duration: span };
}

/** A route change that should NOT animate — see the handoff's rules. */
export function shouldSkip({ from, to, navigationType, documentHidden = false }) {
  if (!from) return true;                       // first paint, or a deep link straight in
  if (documentHidden) return true;              // back/forward in a tab nobody is looking at
  if (from.pathname === to.pathname) return true;   // a tab you are already on, or an anchor
  return false;
}

export function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  catch { return false; }
}
